import { ReactElement, useState } from 'react';
import { Formik, Form } from 'formik';
import PhoneNumberField from './Fields/PhoneField';
import InputField from './Fields/InputField';
import Button from '../Button';
import {
  invoiceValidationEN,
  invoiceValidationES,
} from '../../validations/invoice';
import IdTypeSelect from './Controls/IdTypeSelect';
import { AiOutlineLoading } from 'react-icons/ai';
import { useRouter } from 'next/router';
import * as Yup from 'yup';

export type BillingInformation = {
  firstName: string;
  lastName: string;
  email: string;
  idType?: string;
  idNumber?: string;
  address?: string;
  phoneNumber: {
    number: string;
    countryDialCode: string;
    countryCode: string;
  };
  orderId: string;
};

type BillingInfoFormProps = {
  eventId: string;
  quantity: number;
  onSubmit: (x: BillingInformation, e: any) => void;
  initialValues: BillingInformation;
  userEmail?: string;
  isFree?: boolean; // Indicates if the event is free
};

const BillingInfoForm = ({
  eventId,
  quantity,
  onSubmit,
  initialValues,
  userEmail,
  isFree = false,
}: BillingInfoFormProps): ReactElement => {
  const router = useRouter();
  const [submitting, setSubmitting] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const referral = router.query.referral;
  const language = router.locale as SupportedLanguages;
  const isSpanish = language === 'es';

  // Create simplified validation schema for free events
  const createFreeEventValidationSchema = () => {
    if (isSpanish) {
      return Yup.object({
        firstName: Yup.string().required('El nombre es requerido'),
        lastName: Yup.string().required('El apellido es requerido'),
        email: Yup.string()
          .email('Email inválido')
          .required('El email es requerido'),
        phoneNumber: Yup.object({
          number: Yup.string().required('El número es requerido'),
          countryDialCode: Yup.string().required('Requerido'),
          countryCode: Yup.string().required('Requerido'),
        }),
      });
    } else {
      return Yup.object({
        firstName: Yup.string().required('First name is required'),
        lastName: Yup.string().required('Last name is required'),
        email: Yup.string()
          .email('Invalid email')
          .required('Email is required'),
        phoneNumber: Yup.object({
          number: Yup.string().required('Phone number is required'),
          countryDialCode: Yup.string().required('Required'),
          countryCode: Yup.string().required('Required'),
        }),
      });
    }
  };

  return (
    <div className="flex flex-col items-center">
      <Formik
        initialValues={{
          firstName: initialValues.firstName || '',
          lastName: initialValues.lastName || '',
          email: initialValues.email || '',
          idType: initialValues.idType || 'CITIZENSHIP_CARD',
          idNumber: initialValues.idNumber || '',
          address: initialValues.address || '',
          phoneNumber: {
            number: initialValues.phoneNumber?.number || '',
            countryDialCode: initialValues.phoneNumber?.countryDialCode || '',
            countryCode: initialValues.phoneNumber?.countryCode || '',
          },
        }}
        enableReinitialize
        onSubmit={async (values, errors) => {
          setSubmitting(true);
          try {
            // For free events, we might want to simplify this too, but keeping the existing logic for now
            const [response, billingInfoResponse] = await Promise.all([
              fetch('/api/payments/order-id', {
                method: 'POST',
                body: JSON.stringify({
                  user_phone_number: values.phoneNumber?.number || '',
                  user_email: values.email,
                  event_id: eventId,
                  number_invitees: Number(quantity),
                  referral_code: referral,
                  billing_info: values,
                  is_free: isFree,
                }),
                headers: {
                  'Content-Type': 'application/json',
                },
              }),
              fetch(`/api/billing-info`, {
                headers: {
                  'Content-Type': 'application/json',
                },
                method: 'POST',
                body: JSON.stringify({
                  ...values,
                  userEmail,
                }),
              }),
            ]);
            const data = await response.json();
            const error = data?.error;
            onSubmit(
              {
                ...values,
                orderId: data?.payment?.reference,
              },
              error
            );
          } catch (e) {
            console.log(e);
          }
          setSubmitting(false);
        }}
        validationSchema={
          isFree
            ? createFreeEventValidationSchema()
            : isSpanish
            ? invoiceValidationES
            : invoiceValidationEN
        }
      >
        {({ handleSubmit, errors, values }) => {
          return (
            <Form className="w-full flex flex-col items-center">
              <PhoneNumberField
                name="phoneNumber"
                label={isSpanish ? 'Celular' : 'Phone'}
                labeled
              />
              <InputField
                name="firstName"
                placeHolder={isSpanish ? 'Nombre' : 'First Name'}
                label={isSpanish ? 'Nombre' : 'First Name'}
                labeled
              />
              <InputField
                name="lastName"
                placeHolder={isSpanish ? 'Apellido' : 'Last Name'}
                label={isSpanish ? 'Apellido' : 'Last Name'}
                labeled
              />
              <InputField
                name="email"
                placeHolder="E-mail"
                label="E-mail"
                labeled
              />

              {/* Only show these fields if the event is NOT free */}
              {!isFree && (
                <>
                  <IdTypeSelect name="idType" />
                  <InputField
                    name="idNumber"
                    placeHolder={isSpanish ? 'No. Identificación' : 'ID Number'}
                    label={isSpanish ? 'No. Identificación' : 'ID Number'}
                    labeled
                    type="number"
                  />
                  <InputField
                    name="address"
                    placeHolder={isSpanish ? 'Dirección' : 'Address'}
                    label={isSpanish ? 'Dirección' : 'Address'}
                    labeled
                  />
                </>
              )}

              {isSpanish ? (
                <div className="text-sm text-center pb-6">
                  Al enviar tu información aceptas los{' '}
                  <span
                    className="text-copper underline"
                    onClick={() => {
                      setShowTerms(true);
                    }}
                  >
                    términos y condiciones
                  </span>{' '}
                  de los datos registrados en este sitio.
                </div>
              ) : (
                <div className="text-sm text-center pb-6">
                  By sending your information you accept the{' '}
                  <span
                    className="text-copper underline"
                    onClick={() => {
                      setShowTerms(true);
                    }}
                  >
                    terms and conditions
                  </span>{' '}
                  of the data registered on this site.
                </div>
              )}
              <div className="flex pb-6 w-full justify-center">
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  id="billing-info-form-submit"
                >
                  {submitting ? (
                    <div className="w-full flex justify-center items-center h-5">
                      <AiOutlineLoading className="animate-spin text-white text-sm" />
                    </div>
                  ) : isSpanish ? (
                    'Continuar'
                  ) : (
                    'Continue'
                  )}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default BillingInfoForm;
